import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

/**
 * @description Bridges each module's "export test session" data (built from that module's own
 * result page — often local React state NavigationBar has no access to, e.g. CSqueeze's drawn
 * baseline) to the standardized export icon shown in NavigationBar. A result page registers
 * `{ module, exportRef }` on mount (see `useRegisterSessionExport`); NavigationBar shows the icon
 * only while the registered `module` matches the current URL and clears it once nothing's
 * registered for that route.
 */
const SessionExportContext = createContext({
  config: null,
  setConfig: () => {},
  geoJsonConfig: null,
  setGeoJsonConfig: () => {},
  pdfConfig: null,
  setPdfConfig: () => {},
});

export const SessionExportProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [geoJsonConfig, setGeoJsonConfig] = useState(null);
  const [pdfConfig, setPdfConfig] = useState(null);
  return (
    <SessionExportContext.Provider
      value={{ config, setConfig, geoJsonConfig, setGeoJsonConfig, pdfConfig, setPdfConfig }}>
      {children}
    </SessionExportContext.Provider>
  );
};

export const useSessionExport = () => useContext(SessionExportContext);

/**
 * @function useRegisterExport
 * @description The registration both export icons share: keeps a ref pointing at the latest
 * closure on every render, so the handler always sees current state, while only mount and unmount
 * touch the shared context.
 * @param {Function} setConfig - The context setter to publish into.
 * @param {String} module - Module key matching NavigationBar's URL-derived key.
 * @param {Function|null|undefined} exportFn - The handler, or a falsy value to register nothing.
 * **Called by:
 * @see {useRegisterSessionExport}
 * @see {useRegisterGeoJsonExport}
 * @memberof module:Core
 * @returns {void}
 */
const useRegisterExport = (setConfig, module, exportFn) => {
  const exportRef = useRef(exportFn);
  exportRef.current = exportFn;

  const enabled = Boolean(exportFn);

  useEffect(() => {
    if (!enabled) return undefined;
    setConfig({ module, exportRef });
    return () => setConfig((current) => (current?.exportRef === exportRef ? null : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, enabled]);
};

/**
 * @function useRegisterGeoJsonExport
 * @description Registers a result page's GeoJSON builder as the one NavigationBar's GIS export
 * icon calls, on the same terms as the session export beside it: registered on mount, dropped on
 * unmount, and shown only on the module it belongs to.
 * Kept apart from the session export rather than folded into it because the two answer different
 * questions - one reproduces the session inside CASSIE, the other hands the geometry to a GIS -
 * and a module can have something to offer for one and not the other.
 * @param {String} module - Module key matching NavigationBar's URL-derived key.
 * @param {Function|null|undefined} exportFn - Builds and downloads the GeoJSON. Pass a falsy value
 *  while the analysis has produced no vector layers, and no icon is shown.
 * **Called by:
 * @see {ProcessingPage} - each module's result page.
 * @memberof module:Core
 * @returns {void}
 */
export const useRegisterGeoJsonExport = (module, exportFn) => {
  const { setGeoJsonConfig } = useContext(SessionExportContext);
  useRegisterExport(setGeoJsonConfig, module, exportFn);
};

/**
 * @function useRegisterSessionExport
 * @description Registers a result page's own `exportSessionState` as the one NavigationBar's
 * standardized export icon calls, for as long as this component stays mounted. Keeps a ref
 * updated with the latest closure every render (so it always sees current state) without
 * re-registering on every render — only the mount/unmount touch the shared context.
 * @param {String} module - Module key matching NavigationBar's URL-derived key (e.g. 'csqueeze').
 * @param {Function|null|undefined} exportFn - The module's own `exportSessionState`, redefined
 * each render. Pass a falsy value (e.g. while there's nothing to export yet) to unregister —
 * NavigationBar then shows no export icon for this module.
 * **Called by:
 * @see {CSqueeze/result.jsx}, {Bathymetry/result.jsx}, {CoastlineEvolutionDialog}
 * @memberof module:Core
 * @returns {void}
 */
export const useRegisterSessionExport = (module, exportFn) => {
  const { setConfig } = useContext(SessionExportContext);
  useRegisterExport(setConfig, module, exportFn);
};

/**
 * @function useRegisterPdfExport
 * @description Registers a result page's PDF report builder as the one NavigationBar's report
 * button calls, on the same terms as the two exports beside it.
 * Separate from them for the same reason they are separate from each other: the three answer
 * different questions. The session export reproduces the analysis inside CASSIE, the GeoJSON hands
 * the geometry to a GIS, and this one hands the whole result - area, imagery, model, metrics and
 * maps - to a person, in one document they can read without the app.
 * @param {String} module - Module key matching NavigationBar's URL-derived key.
 * @param {Function|null|undefined} exportFn - Builds and downloads the PDF. Pass a falsy value
 *  while there is no finished result, and no button is shown.
 * **Called by:
 * @see {ProcessingPage} - each module's result page.
 * @memberof module:Core
 * @returns {void}
 */
export const useRegisterPdfExport = (module, exportFn) => {
  const { setPdfConfig } = useContext(SessionExportContext);
  useRegisterExport(setPdfConfig, module, exportFn);
};
